import React from 'react';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { type VariantProps, tv } from 'tailwind-variants';

import LazyLoadedImage from 'common/LazyLoadedImage';
import { cn } from 'common/style/utils';

import type { LucideProps } from 'lucide-react';

const dropdownMenuItemVariants = tv({
  slots: {
    base: [
      'relative rounded-sm',
      'flex flex-row items-center',
      'h-8 px-2.5 py-0 gap-2',
      'select-none outline-none cursor-pointer',
      'transition-all duration-200 ease-in-out',
      'text-foreground',
    ],
    label: 'text-body',
  },
  variants: {
    variant: {
      default: {
        base: [
          'hover:bg-indigo-30 hover:text-indigo-90',
          'dark:hover:bg-input/50 dark:hover:text-primary-foreground',
        ],
      },
      destructive: {
        base: ['text-red-110', 'hover:bg-red-30'],
      },
    },
    selected: {
      true: 'text-canny',
      false: '',
    },
    disabled: {
      true: {
        base: '!text-gray-80 hover:bg-transparent cursor-not-allowed',
      },
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    selected: false,
    disabled: false,
  },
});

function DropdownMenu(
  props: Omit<React.ComponentProps<typeof DropdownMenuPrimitive.Root>, 'className'>,
) {
  return <DropdownMenuPrimitive.Root {...props} />;
}

function DropdownMenuTrigger({
  asChild = true,
  ...props
}: Omit<React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>, 'className'>) {
  return <DropdownMenuPrimitive.Trigger asChild={asChild} {...props} />;
}

function DropdownMenuContent({
  sideOffset = 4,
  fitTrigger = true,
  ...props
}: Omit<React.ComponentProps<typeof DropdownMenuPrimitive.Content>, 'className'> & {
  fitTrigger?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        onCloseAutoFocus={(e) => e.preventDefault()}
        sideOffset={sideOffset}
        className={cn(
          'bg-popover box-border base-border z-3000',
          'flex relative flex-col gap-1 p-1 rounded-sm',
          'min-w-[170px] [will-change:transform,opacity]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'max-h-(--radix-dropdown-menu-content-available-height)',
          'origin-(--radix-dropdown-menu-content-transform-origin)',
          {
            'min-w-[var(--radix-popper-anchor-width)]': fitTrigger,
          },
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

export type DropdownMenuItemProps = Omit<
  React.ComponentProps<typeof DropdownMenuPrimitive.Item>,
  'className' | 'children'
> &
  VariantProps<typeof dropdownMenuItemVariants> & {
    onSelect?: () => void;
    icon?: React.ComponentType<LucideProps>;
    iconSize?: number;
    iconStrokeWidth?: number;
    imageIcon?: string;
    label: string;
  };

function DropdownMenuItem({
  variant,
  selected,
  disabled,
  onSelect,
  icon,
  iconSize = 14,
  imageIcon,
  label,
  ...props
}: DropdownMenuItemProps) {
  const style = dropdownMenuItemVariants({ variant, selected, disabled });

  return (
    <DropdownMenuPrimitive.Item
      className={style.base()}
      disabled={disabled}
      onSelect={onSelect}
      {...props}>
      <MenuItemIcon icon={icon} iconSize={iconSize} imageIcon={imageIcon} />
      <span className={style.label()}>{label}</span>
    </DropdownMenuPrimitive.Item>
  );
}

type MenuItemIconProps = {
  icon?: React.ComponentType<LucideProps>;
  iconSize?: number;
  iconStrokeWidth?: number;
  imageIcon?: string;
};

function MenuItemIcon({
  icon: Icon,
  iconSize = 14,
  iconStrokeWidth = 1.5,
  imageIcon,
}: MenuItemIconProps) {
  if (Icon) {
    return <Icon size={iconSize} strokeWidth={iconStrokeWidth} />;
  }

  if (imageIcon) {
    return (
      <LazyLoadedImage
        className="flex items-center justify-center size-4"
        src={imageIcon}
        alt="icon"
      />
    );
  }

  return null;
}

DropdownMenu.Content = DropdownMenuContent;
DropdownMenu.Trigger = DropdownMenuTrigger;
DropdownMenu.Item = DropdownMenuItem;

export default DropdownMenu;
